import { useMemo } from 'react';

import { GenericStatusEnum, ThirdPartyTypeEnum } from '@e-socna/data-layer';
import { isDefined } from '@e-socna/utils';

import { useFindAllOrganizationsQuery } from './useFindAllOrganizationsQuery.hook';
import { useFindAllDeletedOrganizationsQuery } from './useFindAllDeletedOrganizationsQuery.hook';
import { useFindAllDeletedExternalOrganizationsQuery } from './useFindAllDeletedExternalOrganizationsQuery.hook';
import { useFindAllDeletedExternalPeopleQuery } from './useFindAllDeletedExternalPeopleQuery.hook';
import { useFindAllDeletedExternalThirdPartiesQuery } from './useFindAllDeletedExternalThirdPartiesQuery.hook';
import { useFindAllDeletedInternalOrganizationsQuery } from './useFindAllDeletedInternalOrganizationsQuery.hook';
import { useFindAllDeletedInternalPeopleQuery } from './useFindAllDeletedInternalPeopleQuery.hook';
import { useFindAllDeletedInternalThirdPartiesQuery } from './useFindAllDeletedInternalThirdPartiesQuery.hook';
import { useFindAllDeletedPeopleQuery } from './useFindAllDeletedPeopleQuery.hook';
import { useFindAllDeletedThirdPartiesQuery } from './useFindAllDeletedThirdPartiesQuery.hook';
import { useFindAllDisabledOrganizationsQuery } from './useFindAllDisabledOrganizationsQuery.hook';
import { useFindAllDisabledExternalOrganizationsQuery } from './useFindAllDisabledExternalOrganizationsQuery.hook';
import { useFindAllDisabledExternalPeopleQuery } from './useFindAllDisabledExternalPeopleQuery.hook';
import { useFindAllDisabledExternalThirdPartiesQuery } from './useFindAllDisabledExternalThirdPartiesQuery.hook';
import { useFindAllDisabledInternalOrganizationsQuery } from './useFindAllDisabledInternalOrganizationsQuery.hook';
import { useFindAllDisabledInternalPeopleQuery } from './useFindAllDisabledInternalPeopleQuery.hook';
import { useFindAllDisabledInternalThirdPartiesQuery } from './useFindAllDisabledInternalThirdPartiesQuery.hook';
import { useFindAllDisabledPeopleQuery } from './useFindAllDisabledPeopleQuery.hook';
import { useFindAllDisabledThirdPartiesQuery } from './useFindAllDisabledThirdPartiesQuery.hook';
import { useFindAllEnabledOrganizationsQuery } from './useFindAllEnabledOrganizationsQuery.hook';
import { useFindAllEnabledExternalOrganizationsQuery } from './useFindAllEnabledExternalOrganizationsQuery.hook';
import { useFindAllEnabledExternalPeopleQuery } from './useFindAllEnabledExternalPeopleQuery.hook';
import { useFindAllEnabledExternalThirdPartiesQuery } from './useFindAllEnabledExternalThirdPartiesQuery.hook';
import { useFindAllEnabledInternalOrganizationsQuery } from './useFindAllEnabledInternalOrganizationsQuery.hook';
import { useFindAllEnabledInternalPeopleQuery } from './useFindAllEnabledInternalPeopleQuery.hook';
import { useFindAllEnabledInternalThirdPartiesQuery } from './useFindAllEnabledInternalThirdPartiesQuery.hook';
import { useFindAllEnabledPeopleQuery } from './useFindAllEnabledPeopleQuery.hook';
import { useFindAllEnabledThirdPartiesQuery } from './useFindAllEnabledThirdPartiesQuery.hook';
import { useFindAllExternalOrganizationsQuery } from './useFindAllExternalOrganizationsQuery.hook';
import { useFindAllExternalPeopleQuery } from './useFindAllExternalPeopleQuery.hook';
import { useFindAllExternalThirdPartiesQuery } from './useFindAllExternalThirdPartiesQuery.hook';
import { useFindAllInternalOrganizationsQuery } from './useFindAllInternalOrganizationsQuery.hook';
import { useFindAllInternalPeopleQuery } from './useFindAllInternalPeopleQuery.hook';
import { useFindAllInternalThirdPartiesQuery } from './useFindAllInternalThirdPartiesQuery.hook';
import { useFindAllPeopleQuery } from './useFindAllPeopleQuery.hook';
import { useFindAllThirdPartiesQuery } from './useFindAllThirdPartiesQuery.hook';

interface Props {
  readonly isInternal?: boolean;
  readonly kind?: ThirdPartyTypeEnum;
  readonly status?: GenericStatusEnum;
}

export const useThirdPartiesQuery = ({
  isInternal,
  kind,
  status,
}: Readonly<Props>) => {
  const thirdPartiesQuery = useMemo((): string => {
    if (kind === ThirdPartyTypeEnum.Organization) {
      if (!isDefined(status)) {
        if (!isDefined(isInternal)) {
          return 'useFindAllOrganizationsQuery';
        } else if (!isInternal) {
          return 'useFindAllExternalOrganizationsQuery';
        }
        return 'useFindAllInternalOrganizationsQuery';
      } else if (status === GenericStatusEnum.Deleted) {
        if (!isDefined(isInternal)) {
          return 'useFindAllDeletedOrganizationsQuery';
        } else if (!isInternal) {
          return 'useFindAllDeletedExternalOrganizationsQuery';
        }
        return 'useFindAllDeletedInternalOrganizationsQuery';
      } else if (status === GenericStatusEnum.Disabled) {
        if (!isDefined(isInternal)) {
          return 'useFindAllDisabledOrganizationsQuery';
        } else if (!isInternal) {
          return 'useFindAllDisabledExternalOrganizationsQuery';
        }
        return 'useFindAllDisabledInternalOrganizationsQuery';
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      } else if (status === GenericStatusEnum.Enabled) {
        if (!isDefined(isInternal)) {
          return 'useFindAllEnabledOrganizationsQuery';
        } else if (!isInternal) {
          return 'useFindAllEnabledExternalOrganizationsQuery';
        }
        return 'useFindAllEnabledInternalOrganizationsQuery';
      }
    } else if (kind === ThirdPartyTypeEnum.Person) {
      if (!isDefined(status)) {
        if (!isDefined(isInternal)) {
          return 'useFindAllPeopleQuery';
        } else if (!isInternal) {
          return 'useFindAllExternalPeopleQuery';
        }
        return 'useFindAllInternalPeopleQuery';
      } else if (status === GenericStatusEnum.Deleted) {
        if (!isDefined(isInternal)) {
          return 'useFindAllDeletedPeopleQuery';
        } else if (!isInternal) {
          return 'useFindAllDeletedExternalPeopleQuery';
        }
        return 'useFindAllDeletedInternalPeopleQuery';
      } else if (status === GenericStatusEnum.Disabled) {
        if (!isDefined(isInternal)) {
          return 'useFindAllDisabledPeopleQuery';
        } else if (!isInternal) {
          return 'useFindAllDisabledExternalPeopleQuery';
        }
        return 'useFindAllDisabledInternalPeopleQuery';
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      } else if (status === GenericStatusEnum.Enabled) {
        if (!isDefined(isInternal)) {
          return 'useFindAllEnabledPeopleQuery';
        } else if (!isInternal) {
          return 'useFindAllEnabledExternalPeopleQuery';
        }
        return 'useFindAllEnabledInternalPeopleQuery';
      }
    } else if (!isDefined(kind)) {
      if (!isDefined(status)) {
        if (!isDefined(isInternal)) {
          return 'useFindAllThirdPartiesQuery';
        } else if (!isInternal) {
          return 'useFindAllExternalThirdPartiesQuery';
        }
        return 'useFindAllInternalThirdPartiesQuery';
      } else if (status === GenericStatusEnum.Deleted) {
        if (!isDefined(isInternal)) {
          return 'useFindAllDeletedThirdPartiesQuery';
        } else if (!isInternal) {
          return 'useFindAllDeletedExternalThirdPartiesQuery';
        }
        return 'useFindAllDeletedInternalThirdPartiesQuery';
      } else if (status === GenericStatusEnum.Disabled) {
        if (!isDefined(isInternal)) {
          return 'useFindAllDisabledThirdPartiesQuery';
        } else if (!isInternal) {
          return 'useFindAllDisabledExternalThirdPartiesQuery';
        }
        return 'useFindAllDisabledInternalThirdPartiesQuery';
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      } else if (status === GenericStatusEnum.Enabled) {
        if (!isDefined(isInternal)) {
          return 'useFindAllEnabledThirdPartiesQuery';
        } else if (!isInternal) {
          return 'useFindAllEnabledExternalThirdPartiesQuery';
        }
        return 'useFindAllEnabledInternalThirdPartiesQuery';
      }
    }
    throw new Error('Invalid query');
  }, [isInternal, kind, status]);

  const {
    data: dataFindAllOrganizationsQuery,
    isLoading: isLoadingFindAllOrganizationsQuery,
    isSuccess: isSuccessFindAllOrganizationsQuery,
  } = useFindAllOrganizationsQuery(
    thirdPartiesQuery === 'useFindAllOrganizationsQuery',
  );

  const {
    data: dataFindAllDeletedOrganizationsQuery,
    isLoading: isLoadingFindAllDeletedOrganizationsQuery,
    isSuccess: isSuccessFindAllDeletedOrganizationsQuery,
  } = useFindAllDeletedOrganizationsQuery(
    thirdPartiesQuery === 'useFindAllDeletedOrganizationsQuery',
  );

  const {
    data: dataFindAllDeletedExternalOrganizationsQuery,
    isLoading: isLoadingFindAllDeletedExternalOrganizationsQuery,
    isSuccess: isSuccessFindAllDeletedExternalOrganizationsQuery,
  } = useFindAllDeletedExternalOrganizationsQuery(
    thirdPartiesQuery === 'useFindAllDeletedExternalOrganizationsQuery',
  );

  const {
    data: dataFindAllDeletedExternalPeopleQuery,
    isLoading: isLoadingFindAllDeletedExternalPeopleQuery,
    isSuccess: isSuccessFindAllDeletedExternalPeopleQuery,
  } = useFindAllDeletedExternalPeopleQuery(
    thirdPartiesQuery === 'useFindAllDeletedExternalPeopleQuery',
  );

  const {
    data: dataFindAllDeletedExternalThirdPartiesQuery,
    isLoading: isLoadingFindAllDeletedExternalThirdPartiesQuery,
    isSuccess: isSuccessFindAllDeletedExternalThirdPartiesQuery,
  } = useFindAllDeletedExternalThirdPartiesQuery(
    thirdPartiesQuery === 'useFindAllDeletedExternalThirdPartiesQuery',
  );

  const {
    data: dataFindAllDeletedInternalOrganizationsQuery,
    isLoading: isLoadingFindAllDeletedInternalOrganizationsQuery,
    isSuccess: isSuccessFindAllDeletedInternalOrganizationsQuery,
  } = useFindAllDeletedInternalOrganizationsQuery(
    thirdPartiesQuery === 'useFindAllDeletedInternalOrganizationsQuery',
  );

  const {
    data: dataFindAllDeletedInternalPeopleQuery,
    isLoading: isLoadingFindAllDeletedInternalPeopleQuery,
    isSuccess: isSuccessFindAllDeletedInternalPeopleQuery,
  } = useFindAllDeletedInternalPeopleQuery(
    thirdPartiesQuery === 'useFindAllDeletedInternalPeopleQuery',
  );

  const {
    data: dataFindAllDeletedInternalThirdPartiesQuery,
    isLoading: isLoadingFindAllDeletedInternalThirdPartiesQuery,
    isSuccess: isSuccessFindAllDeletedInternalThirdPartiesQuery,
  } = useFindAllDeletedInternalThirdPartiesQuery(
    thirdPartiesQuery === 'useFindAllDeletedInternalThirdPartiesQuery',
  );

  const {
    data: dataFindAllDeletedPeopleQuery,
    isLoading: isLoadingFindAllDeletedPeopleQuery,
    isSuccess: isSuccessFindAllDeletedPeopleQuery,
  } = useFindAllDeletedPeopleQuery(
    thirdPartiesQuery === 'useFindAllDeletedPeopleQuery',
  );

  const {
    data: dataFindAllDeletedThirdPartiesQuery,
    isLoading: isLoadingFindAllDeletedThirdPartiesQuery,
    isSuccess: isSuccessFindAllDeletedThirdPartiesQuery,
  } = useFindAllDeletedThirdPartiesQuery(
    thirdPartiesQuery === 'useFindAllDeletedThirdPartiesQuery',
  );

  const {
    data: dataFindAllDisabledOrganizationsQuery,
    isLoading: isLoadingFindAllDisabledOrganizationsQuery,
    isSuccess: isSuccessFindAllDisabledOrganizationsQuery,
  } = useFindAllDisabledOrganizationsQuery(
    thirdPartiesQuery === 'useFindAllDisabledOrganizationsQuery',
  );

  const {
    data: dataFindAllDisabledExternalOrganizationsQuery,
    isLoading: isLoadingFindAllDisabledExternalOrganizationsQuery,
    isSuccess: isSuccessFindAllDisabledExternalOrganizationsQuery,
  } = useFindAllDisabledExternalOrganizationsQuery(
    thirdPartiesQuery === 'useFindAllDisabledExternalOrganizationsQuery',
  );

  const {
    data: dataFindAllDisabledExternalPeopleQuery,
    isLoading: isLoadingFindAllDisabledExternalPeopleQuery,
    isSuccess: isSuccessFindAllDisabledExternalPeopleQuery,
  } = useFindAllDisabledExternalPeopleQuery(
    thirdPartiesQuery === 'useFindAllDisabledExternalPeopleQuery',
  );

  const {
    data: dataFindAllDisabledExternalThirdPartiesQuery,
    isLoading: isLoadingFindAllDisabledExternalThirdPartiesQuery,
    isSuccess: isSuccessFindAllDisabledExternalThirdPartiesQuery,
  } = useFindAllDisabledExternalThirdPartiesQuery(
    thirdPartiesQuery === 'useFindAllDisabledExternalThirdPartiesQuery',
  );

  const {
    data: dataFindAllDisabledInternalOrganizationsQuery,
    isLoading: isLoadingFindAllDisabledInternalOrganizationsQuery,
    isSuccess: isSuccessFindAllDisabledInternalOrganizationsQuery,
  } = useFindAllDisabledInternalOrganizationsQuery(
    thirdPartiesQuery === 'useFindAllDisabledInternalOrganizationsQuery',
  );

  const {
    data: dataFindAllDisabledInternalPeopleQuery,
    isLoading: isLoadingFindAllDisabledInternalPeopleQuery,
    isSuccess: isSuccessFindAllDisabledInternalPeopleQuery,
  } = useFindAllDisabledInternalPeopleQuery(
    thirdPartiesQuery === 'useFindAllDisabledInternalPeopleQuery',
  );

  const {
    data: dataFindAllDisabledInternalThirdPartiesQuery,
    isLoading: isLoadingFindAllDisabledInternalThirdPartiesQuery,
    isSuccess: isSuccessFindAllDisabledInternalThirdPartiesQuery,
  } = useFindAllDisabledInternalThirdPartiesQuery(
    thirdPartiesQuery === 'useFindAllDisabledInternalThirdPartiesQuery',
  );

  const {
    data: dataFindAllDisabledPeopleQuery,
    isLoading: isLoadingFindAllDisabledPeopleQuery,
    isSuccess: isSuccessFindAllDisabledPeopleQuery,
  } = useFindAllDisabledPeopleQuery(
    thirdPartiesQuery === 'useFindAllDisabledPeopleQuery',
  );

  const {
    data: dataFindAllDisabledThirdPartiesQuery,
    isLoading: isLoadingFindAllDisabledThirdPartiesQuery,
    isSuccess: isSuccessFindAllDisabledThirdPartiesQuery,
  } = useFindAllDisabledThirdPartiesQuery(
    thirdPartiesQuery === 'useFindAllDisabledThirdPartiesQuery',
  );

  const {
    data: dataFindAllEnabledOrganizationsQuery,
    isLoading: isLoadingFindAllEnabledOrganizationsQuery,
    isSuccess: isSuccessFindAllEnabledOrganizationsQuery,
  } = useFindAllEnabledOrganizationsQuery(
    thirdPartiesQuery === 'useFindAllEnabledOrganizationsQuery',
  );

  const {
    data: dataFindAllEnabledExternalOrganizationsQuery,
    isLoading,
    isSuccess,
  } = useFindAllEnabledExternalOrganizationsQuery(
    thirdPartiesQuery === 'useFindAllEnabledExternalOrganizationsQuery',
  );

  const {
    data: dataFindAllEnabledExternalPeopleQuery,
    isLoading: isLoadingFindAllEnabledExternalPeopleQuery,
    isSuccess: isSuccessFindAllEnabledExternalPeopleQuery,
  } = useFindAllEnabledExternalPeopleQuery(
    thirdPartiesQuery === 'useFindAllEnabledExternalPeopleQuery',
  );

  const {
    data: dataFindAllEnabledExternalThirdPartiesQuery,
    isLoading: isLoadingFindAllEnabledExternalThirdPartiesQuery,
    isSuccess: isSuccessFindAllEnabledExternalThirdPartiesQuery,
  } = useFindAllEnabledExternalThirdPartiesQuery(
    thirdPartiesQuery === 'useFindAllEnabledExternalThirdPartiesQuery',
  );

  const {
    data: dataFindAllEnabledInternalOrganizationsQuery,
    isLoading: isLoadingFindAllEnabledInternalOrganizationsQuery,
    isSuccess: isSuccessFindAllEnabledInternalOrganizationsQuery,
  } = useFindAllEnabledInternalOrganizationsQuery(
    thirdPartiesQuery === 'useFindAllEnabledInternalOrganizationsQuery',
  );

  const {
    data: dataFindAllEnabledInternalPeopleQuery,
    isLoading: isLoadingFindAllEnabledInternalPeopleQuery,
    isSuccess: isSuccessFindAllEnabledInternalPeopleQuery,
  } = useFindAllEnabledInternalPeopleQuery(
    thirdPartiesQuery === 'useFindAllEnabledInternalPeopleQuery',
  );

  const {
    data: dataFindAllEnabledInternalThirdPartiesQuery,
    isLoading: isLoadingFindAllEnabledInternalThirdPartiesQuery,
    isSuccess: isSuccessFindAllEnabledInternalThirdPartiesQuery,
  } = useFindAllEnabledInternalThirdPartiesQuery(
    thirdPartiesQuery === 'useFindAllEnabledInternalThirdPartiesQuery',
  );

  const {
    data: dataFindAllEnabledPeopleQuery,
    isLoading: isLoadingFindAllEnabledPeopleQuery,
    isSuccess: isSuccessFindAllEnabledPeopleQuery,
  } = useFindAllEnabledPeopleQuery(
    thirdPartiesQuery === 'useFindAllEnabledPeopleQuery',
  );

  const {
    data: dataFindAllEnabledThirdPartiesQuery,
    isLoading: isLoadingFindAllEnabledThirdPartiesQuery,
    isSuccess: isSuccessFindAllEnabledThirdPartiesQuery,
  } = useFindAllEnabledThirdPartiesQuery(
    thirdPartiesQuery === 'useFindAllEnabledThirdPartiesQuery',
  );

  const {
    data: dataFindAllExternalOrganizationsQuery,
    isLoading: isLoadingFindAllExternalOrganizationsQuery,
    isSuccess: isSuccessFindAllExternalOrganizationsQuery,
  } = useFindAllExternalOrganizationsQuery(
    thirdPartiesQuery === 'useFindAllExternalOrganizationsQuery',
  );

  const {
    data: dataFindAllExternalPeopleQuery,
    isLoading: isLoadingFindAllExternalPeopleQuery,
    isSuccess: isSuccessFindAllExternalPeopleQuery,
  } = useFindAllExternalPeopleQuery(
    thirdPartiesQuery === 'useFindAllExternalPeopleQuery',
  );

  const {
    data: dataFindAllExternalThirdPartiesQuery,
    isLoading: isLoadingFindAllExternalThirdPartiesQuery,
    isSuccess: isSuccessFindAllExternalThirdPartiesQuery,
  } = useFindAllExternalThirdPartiesQuery(
    thirdPartiesQuery === 'useFindAllExternalThirdPartiesQuery',
  );

  const {
    data: dataFindAllInternalOrganizationsQuery,
    isLoading: isLoadingFindAllInternalOrganizationsQuery,
    isSuccess: isSuccessFindAllInternalOrganizationsQuery,
  } = useFindAllInternalOrganizationsQuery(
    thirdPartiesQuery === 'useFindAllInternalOrganizationsQuery',
  );

  const {
    data: dataFindAllInternalPeopleQuery,
    isLoading: isLoadingFindAllInternalPeopleQuery,
    isSuccess: isSuccessFindAllInternalPeopleQuery,
  } = useFindAllInternalPeopleQuery(
    thirdPartiesQuery === 'useFindAllInternalPeopleQuery',
  );

  const {
    data: dataFindAllInternalThirdPartiesQuery,
    isLoading: isLoadingFindAllInternalThirdPartiesQuery,
    isSuccess: isSuccessFindAllInternalThirdPartiesQuery,
  } = useFindAllInternalThirdPartiesQuery(
    thirdPartiesQuery === 'useFindAllInternalThirdPartiesQuery',
  );

  const {
    data: dataFindAllPeopleQuery,
    isLoading: isLoadingFindAllPeopleQuery,
    isSuccess: isSuccessFindAllPeopleQuery,
  } = useFindAllPeopleQuery(thirdPartiesQuery === 'useFindAllPeopleQuery');

  const {
    data: dataFindAllThirdPartiesQuery,
    isLoading: isLoadingFindAllThirdPartiesQuery,
    isSuccess: isSuccessFindAllThirdPartiesQuery,
  } = useFindAllThirdPartiesQuery(
    thirdPartiesQuery === 'useFindAllThirdPartiesQuery',
  );

  switch (thirdPartiesQuery) {
    case 'useFindAllOrganizationsQuery':
      return {
        data: dataFindAllOrganizationsQuery,
        isLoading: isLoadingFindAllOrganizationsQuery,
        isSuccess: isSuccessFindAllOrganizationsQuery,
      };
    case 'useFindAllDeletedOrganizationsQuery':
      return {
        data: dataFindAllDeletedOrganizationsQuery,
        isLoading: isLoadingFindAllDeletedOrganizationsQuery,
        isSuccess: isSuccessFindAllDeletedOrganizationsQuery,
      };
    case 'useFindAllDeletedExternalOrganizationsQuery':
      return {
        data: dataFindAllDeletedExternalOrganizationsQuery,
        isLoading: isLoadingFindAllDeletedExternalOrganizationsQuery,
        isSuccess: isSuccessFindAllDeletedExternalOrganizationsQuery,
      };
    case 'useFindAllDeletedExternalPeopleQuery':
      return {
        data: dataFindAllDeletedExternalPeopleQuery,
        isLoading: isLoadingFindAllDeletedExternalPeopleQuery,
        isSuccess: isSuccessFindAllDeletedExternalPeopleQuery,
      };
    case 'useFindAllDeletedExternalThirdPartiesQuery':
      return {
        data: dataFindAllDeletedExternalThirdPartiesQuery,
        isLoading: isLoadingFindAllDeletedExternalThirdPartiesQuery,
        isSuccess: isSuccessFindAllDeletedExternalThirdPartiesQuery,
      };
    case 'useFindAllDeletedInternalOrganizationsQuery':
      return {
        data: dataFindAllDeletedInternalOrganizationsQuery,
        isLoading: isLoadingFindAllDeletedInternalOrganizationsQuery,
        isSuccess: isSuccessFindAllDeletedInternalOrganizationsQuery,
      };
    case 'useFindAllDeletedInternalPeopleQuery':
      return {
        data: dataFindAllDeletedInternalPeopleQuery,
        isLoading: isLoadingFindAllDeletedInternalPeopleQuery,
        isSuccess: isSuccessFindAllDeletedInternalPeopleQuery,
      };
    case 'useFindAllDeletedInternalThirdPartiesQuery':
      return {
        data: dataFindAllDeletedInternalThirdPartiesQuery,
        isLoading: isLoadingFindAllDeletedInternalThirdPartiesQuery,
        isSuccess: isSuccessFindAllDeletedInternalThirdPartiesQuery,
      };
    case 'useFindAllDeletedPeopleQuery':
      return {
        data: dataFindAllDeletedPeopleQuery,
        isLoading: isLoadingFindAllDeletedPeopleQuery,
        isSuccess: isSuccessFindAllDeletedPeopleQuery,
      };
    case 'useFindAllDeletedThirdPartiesQuery':
      return {
        data: dataFindAllDeletedThirdPartiesQuery,
        isLoading: isLoadingFindAllDeletedThirdPartiesQuery,
        isSuccess: isSuccessFindAllDeletedThirdPartiesQuery,
      };
    case 'useFindAllDisabledOrganizationsQuery':
      return {
        data: dataFindAllDisabledOrganizationsQuery,
        isLoading: isLoadingFindAllDisabledOrganizationsQuery,
        isSuccess: isSuccessFindAllDisabledOrganizationsQuery,
      };
    case 'useFindAllDisabledExternalOrganizationsQuery':
      return {
        data: dataFindAllDisabledExternalOrganizationsQuery,
        isLoading: isLoadingFindAllDisabledExternalOrganizationsQuery,
        isSuccess: isSuccessFindAllDisabledExternalOrganizationsQuery,
      };
    case 'useFindAllDisabledExternalPeopleQuery':
      return {
        data: dataFindAllDisabledExternalPeopleQuery,
        isLoading: isLoadingFindAllDisabledExternalPeopleQuery,
        isSuccess: isSuccessFindAllDisabledExternalPeopleQuery,
      };
    case 'useFindAllDisabledExternalThirdPartiesQuery':
      return {
        data: dataFindAllDisabledExternalThirdPartiesQuery,
        isLoading: isLoadingFindAllDisabledExternalThirdPartiesQuery,
        isSuccess: isSuccessFindAllDisabledExternalThirdPartiesQuery,
      };
    case 'useFindAllDisabledInternalOrganizationsQuery':
      return {
        data: dataFindAllDisabledInternalOrganizationsQuery,
        isLoading: isLoadingFindAllDisabledInternalOrganizationsQuery,
        isSuccess: isSuccessFindAllDisabledInternalOrganizationsQuery,
      };
    case 'useFindAllDisabledInternalPeopleQuery':
      return {
        data: dataFindAllDisabledInternalPeopleQuery,
        isLoading: isLoadingFindAllDisabledInternalPeopleQuery,
        isSuccess: isSuccessFindAllDisabledInternalPeopleQuery,
      };
    case 'useFindAllDisabledInternalThirdPartiesQuery':
      return {
        data: dataFindAllDisabledInternalThirdPartiesQuery,
        isLoading: isLoadingFindAllDisabledInternalThirdPartiesQuery,
        isSuccess: isSuccessFindAllDisabledInternalThirdPartiesQuery,
      };
    case 'useFindAllDisabledPeopleQuery':
      return {
        data: dataFindAllDisabledPeopleQuery,
        isLoading: isLoadingFindAllDisabledPeopleQuery,
        isSuccess: isSuccessFindAllDisabledPeopleQuery,
      };
    case 'useFindAllDisabledThirdPartiesQuery':
      return {
        data: dataFindAllDisabledThirdPartiesQuery,
        isLoading: isLoadingFindAllDisabledThirdPartiesQuery,
        isSuccess: isSuccessFindAllDisabledThirdPartiesQuery,
      };
    case 'useFindAllEnabledOrganizationsQuery':
      return {
        data: dataFindAllEnabledOrganizationsQuery,
        isLoading: isLoadingFindAllEnabledOrganizationsQuery,
        isSuccess: isSuccessFindAllEnabledOrganizationsQuery,
      };
    case 'useFindAllEnabledExternalOrganizationsQuery':
      return {
        data: dataFindAllEnabledExternalOrganizationsQuery,
        isLoading,
        isSuccess,
      };
    case 'useFindAllEnabledExternalPeopleQuery':
      return {
        data: dataFindAllEnabledExternalPeopleQuery,
        isLoading: isLoadingFindAllEnabledExternalPeopleQuery,
        isSuccess: isSuccessFindAllEnabledExternalPeopleQuery,
      };
    case 'useFindAllEnabledExternalThirdPartiesQuery':
      return {
        data: dataFindAllEnabledExternalThirdPartiesQuery,
        isLoading: isLoadingFindAllEnabledExternalThirdPartiesQuery,
        isSuccess: isSuccessFindAllEnabledExternalThirdPartiesQuery,
      };
    case 'useFindAllEnabledInternalOrganizationsQuery':
      return {
        data: dataFindAllEnabledInternalOrganizationsQuery,
        isLoading: isLoadingFindAllEnabledInternalOrganizationsQuery,
        isSuccess: isSuccessFindAllEnabledInternalOrganizationsQuery,
      };
    case 'useFindAllEnabledInternalPeopleQuery':
      return {
        data: dataFindAllEnabledInternalPeopleQuery,
        isLoading: isLoadingFindAllEnabledInternalPeopleQuery,
        isSuccess: isSuccessFindAllEnabledInternalPeopleQuery,
      };
    case 'useFindAllEnabledInternalThirdPartiesQuery':
      return {
        data: dataFindAllEnabledInternalThirdPartiesQuery,
        isLoading: isLoadingFindAllEnabledInternalThirdPartiesQuery,
        isSuccess: isSuccessFindAllEnabledInternalThirdPartiesQuery,
      };
    case 'useFindAllEnabledPeopleQuery':
      return {
        data: dataFindAllEnabledPeopleQuery,
        isLoading: isLoadingFindAllEnabledPeopleQuery,
        isSuccess: isSuccessFindAllEnabledPeopleQuery,
      };
    case 'useFindAllEnabledThirdPartiesQuery':
      return {
        data: dataFindAllEnabledThirdPartiesQuery,
        isLoading: isLoadingFindAllEnabledThirdPartiesQuery,
        isSuccess: isSuccessFindAllEnabledThirdPartiesQuery,
      };
    case 'useFindAllExternalOrganizationsQuery':
      return {
        data: dataFindAllExternalOrganizationsQuery,
        isLoading: isLoadingFindAllExternalOrganizationsQuery,
        isSuccess: isSuccessFindAllExternalOrganizationsQuery,
      };
    case 'useFindAllExternalPeopleQuery':
      return {
        data: dataFindAllExternalPeopleQuery,
        isLoading: isLoadingFindAllExternalPeopleQuery,
        isSuccess: isSuccessFindAllExternalPeopleQuery,
      };
    case 'useFindAllExternalThirdPartiesQuery':
      return {
        data: dataFindAllExternalThirdPartiesQuery,
        isLoading: isLoadingFindAllExternalThirdPartiesQuery,
        isSuccess: isSuccessFindAllExternalThirdPartiesQuery,
      };
    case 'useFindAllInternalOrganizationsQuery':
      return {
        data: dataFindAllInternalOrganizationsQuery,
        isLoading: isLoadingFindAllInternalOrganizationsQuery,
        isSuccess: isSuccessFindAllInternalOrganizationsQuery,
      };
    case 'useFindAllInternalPeopleQuery':
      return {
        data: dataFindAllInternalPeopleQuery,
        isLoading: isLoadingFindAllInternalPeopleQuery,
        isSuccess: isSuccessFindAllInternalPeopleQuery,
      };
    case 'useFindAllInternalThirdPartiesQuery':
      return {
        data: dataFindAllInternalThirdPartiesQuery,
        isLoading: isLoadingFindAllInternalThirdPartiesQuery,
        isSuccess: isSuccessFindAllInternalThirdPartiesQuery,
      };
    case 'useFindAllPeopleQuery':
      return {
        data: dataFindAllPeopleQuery,
        isLoading: isLoadingFindAllPeopleQuery,
        isSuccess: isSuccessFindAllPeopleQuery,
      };
    case 'useFindAllThirdPartiesQuery':
      return {
        data: dataFindAllThirdPartiesQuery,
        isLoading: isLoadingFindAllThirdPartiesQuery,
        isSuccess: isSuccessFindAllThirdPartiesQuery,
      };
    default:
      throw new Error(`Unknown query hook: ${thirdPartiesQuery}`);
  }
};
